import { inject, Injectable, OnDestroy } from '@angular/core';
import { TradeKey } from '../core/model/api';
import { RestClientFactory } from '../core/market/rest-client-factory';
import { WebsocketClientFactory, WebsocketClientWrapper } from '../core/market/websocket-client-factory';
import { KeysService } from './keys/keys.service';
import { BinanceWebsocketClient, BybitWebsocketClient, OkxWebsocketClient } from '../core/market';
import { MarketId, TradeKeyWithPermissions } from '../core/model/common';
import { EMPTY, filter, finalize, map, merge, mergeMap, pairwise, startWith, Subscription, tap, withLatestFrom } from 'rxjs';
import { LogEntryFactory, LoggerService } from '../core/service/logger/logger.service';
import { DealHandler } from '../core/trade/realtime/deal.handler';
import { OrderHandler } from '../core/trade/realtime/order.handler';
import { PositionHandler } from '../core/trade/realtime/position.handler';
import { WsPrivateDisconnectEntry } from '../core/service/logger/log-entry';
import { WsKey } from 'libs/binance-api/src';
import { parseFloatSafe } from '../core/func';
import { OrdersFlowService } from '../core/trade/flow/orders-flow.service';
import { OrderHelper } from '../core/trade/order.helper';
import { OrderManagerService } from '../core/trade/manager/order-manager.service';
import { YandexMetricService } from '../core/service/yandex-metric.service';
import { RestoreHistoryService } from '../core/trade/history/restore-history.service';
import { DealNotifyService } from '../core/service/deal-notify.service';

@Injectable({
  providedIn: 'root'
})
export class TerminalService implements OnDestroy {

  private rcf = inject(RestClientFactory)
  private wcf = inject(WebsocketClientFactory)
  private keys = inject(KeysService)
  private logger = inject(LoggerService)
  private logFactory = inject(LogEntryFactory)
  private dealHandler = inject(DealHandler)
  private orderHandler = inject(OrderHandler)
  private orderHelper = inject(OrderHelper)
  private orderManager = inject(OrderManagerService)
  private ordersFlow = inject(OrdersFlowService)
  private positionHandler = inject(PositionHandler)
  private restoreHistory = inject(RestoreHistoryService)
  private dealNotify = inject(DealNotifyService)
  private ym = inject(YandexMetricService)

  private activeConnections = new Map<TradeKey, WebsocketClientWrapper>()
  private activeSubscriptions = new Map<TradeKey, Subscription>()

  private restoredHistoryKeys: number[] = []
  private positionsMap = new Map<string, number>()

  /**
   * Подписываемся на ключи, включаем/выключаем прослушку
   */
  private sb1 = this.keys.keys$.pipe(
    map(keys => keys.filter(k => !k.isDemo && k.canRead)),
    startWith([] as TradeKeyWithPermissions[]),
    pairwise(),
    finalize(() => this.downAll())
  ).subscribe(([prevKeys, currKeys]) => {
    const prevIds = prevKeys.map(k => k.id)
    const currIds = currKeys.map(k => k.id)
    // Отключаем исчезнувшие ключи
    prevKeys.forEach(key => {
      const isDeleted = !currIds.includes(key.id)
      if (isDeleted) {
        this.downKey(key)
      }
    })
    // Подключаем добавленные ключи
    currKeys.forEach(key => {
      const isAdded = !prevIds.includes(key.id)
      if (isAdded) {
        this.upKey(key)
      }
    })
  })

  /**
   * Подписываемся на активный ключ, восстанавливаем историю в логе
   */
  private sb2 = this.keys.activeKey$.pipe(
    filter(key => !this.restoredHistoryKeys.includes(key.id)),
    mergeMap(key => {
      this.restoredHistoryKeys.push(key.id)
      if (key.isDemo || !key.canRead) {
        return EMPTY
      }
      const rest = this.getRestClientForKey(key)
      return this.restoreHistory.restore(rest).pipe(
        tap(({deals, orders, fundings}) => {
          const items1 = deals?.map(deal => this.logFactory.deal(deal)) ?? []
          const items2 = orders?.map(order => this.logFactory.order(order)) ?? []
          const items3 = fundings?.map(funding => this.logFactory.funding(funding)) ?? []
          const logger = this.logger.getKeyLogger(key)
          logger.prepend(items1)
          logger.prepend(items2)
          logger.prepend(items3)
        })
      )
    })
  ).subscribe()

  /**
   * Поднимает подключения для ключа
   */
  private upKey(key: TradeKeyWithPermissions) {
    const ws = this.getWebsocketClientForKey(key)
    if (ws.isBybit) {
      this.connectBybit(key, ws.bybit)
    }
    if (ws.isBinance) {
      this.connectBinance(key, ws.binance)
    }
    if (ws.isOkx) {
      this.connectOkx(key, ws.okx)
    }
    this.dealNotify.addWs(ws)

    const sb = new Subscription()
    // Подписываемся на заявки, сделки и прокидываем их в лог
    sb.add(
      merge(
        this.dealHandler.listen(ws).pipe(
          tap(data => {
            if ('deal' in data) {
              this.logger.getKeyLogger(key).add(this.logFactory.deal(data.deal))
              if (this.orderHelper.isOwnOrderId(data.deal.clientOrderId)) {
                this.ym.registerDeal(data.deal)
              }
            }
            if ('funding' in data) {
              this.logger.getKeyLogger(key).add(this.logFactory.funding(data.funding))
            }
          })
        ),
        this.orderHandler.listen(ws).pipe(
          tap(order => this.logger.getKeyLogger(key).add(this.logFactory.order(order)))
        )
      ).subscribe()
    )
    // Подписываемся на закрытие или переворот позиции, отменяем стоп-заявки для этого инструмента
    if (key.market == 'binance') {
      sb.add(
        this.positionHandler.listen(ws).pipe(
          filter(position => {
            const id = [key.id, position.market, position.board, position.symbol, position.positionSide].join(',')
            const prevSize = this.positionsMap.get(id) ?? 0
            const currSize = parseFloatSafe(position.size)
            if (currSize == 0) {
              this.positionsMap.delete(id)
            } else {
              this.positionsMap.set(id, currSize)
            }
            return currSize == 0 && prevSize != 0 || currSize > 0 && prevSize < 0 || currSize < 0 && prevSize > 0
          }),
          withLatestFrom(this.ordersFlow.getFlow(key).orders$),
          mergeMap(([position, orders]) => {
            const stopOrders = orders.filter(o => this.orderHelper.isOwnStopOrder(o) && o.market == position.market && o.board == position.board && o.symbol == position.symbol && o.positionSide == position.positionSide)
            const cancelOrderObservables = stopOrders.map(stopOrder => this.orderManager.cancelOrder(key, stopOrder))
            return cancelOrderObservables.length ? merge(...cancelOrderObservables) : EMPTY
          })
        ).subscribe()
      )
    }

    this.activeSubscriptions.set(key, sb)
    this.activeConnections.set(key, ws)
  }

  /**
   * Сбрасывает подключения для ключа
   */
  private downKey(key: TradeKey) {
    const ws = this.activeConnections.get(key)
    const sb = this.activeSubscriptions.get(key)
    sb?.unsubscribe()
    if (ws) {
      this.dealNotify.removeWs(ws)
    }
    if (ws?.isBybit) {
      ws.bybit.closeAll()
      ws.bybit.removeAllListeners()
    }
    if (ws?.isBinance) {
      ws.binance.closeAll(false)
      ws.binance.removeAllListeners()
    }
    if (ws?.isOkx) {
      ws.okx.closeAll()
      ws.okx.removeAllListeners()
    }
    this.activeSubscriptions.delete(key)
    this.activeConnections.delete(key)
    this.logger.deleteKeyLogger(key)
  }

  /**
   * Сбрасывает все подключения
   */
  private downAll() {
    this.activeSubscriptions.forEach(sb => sb.unsubscribe())
    this.activeConnections.forEach(ws => {
      if (ws.isBybit) {
        ws.bybit.closeAll()
        ws.bybit.removeAllListeners()
      }
      if (ws.isBinance) {
        ws.binance.closeAll(false)
        ws.binance.removeAllListeners()
      }
      if (ws.isOkx) {
        ws.okx.closeAll()
        ws.okx.removeAllListeners()
      }
    })
    this.activeSubscriptions.clear()
    this.activeConnections.clear()
    this.logger.deleteAllKeyLoggers()
  }

  ngOnDestroy() {
    this.sb1.unsubscribe()
    this.sb2.unsubscribe()
  }

  getRestClientForKey(key: TradeKey) {
    return this.rcf.createForKey(key)
  }

  getWebsocketClientForKey(key: TradeKey) {
    return this.wcf.createForKey(key)
  }

  getRestClientForMarket(market: MarketId) {
    return this.rcf.createForMarket(market)
  }

  getWebsocketClientForMarket(market: MarketId) {
    return this.wcf.createForMarket(market)
  }

  private connectBybit(key: TradeKey, ws: BybitWebsocketClient) {
    let connectionSuccess = false
    let activeWsDisconnectProblem: WsPrivateDisconnectEntry | undefined

    const emitProblem = () => {
      connectionSuccess = false
      if (!activeWsDisconnectProblem) {
        activeWsDisconnectProblem = this.logFactory.wsPrivateDisconnect(key, 'linear')
        this.logger.getKeyLogger(key).add(activeWsDisconnectProblem)
      }
    }
    const resolveProblem = () => {
      if (activeWsDisconnectProblem) {
        activeWsDisconnectProblem.resolve()
        activeWsDisconnectProblem = undefined
      }
      this.logger.getKeyLogger(key).add(this.logFactory.wsPrivateConnect(key, 'linear'))
      connectionSuccess = true
    }

    ws.connectWSAPI()
    ws.connectPrivate().then(() => {
      ws.subscribeV5(['order','position','execution','wallet'], 'linear', true);
    })

    ws.on('update', (data) => {
      console.log('listenBybit: data received', data);
    });

    // Optional: Listen to websocket connection open event
    // (automatic after subscribing to one or more topics)
    ws.on('open', ({ wsKey, event }) => {
      if (!connectionSuccess) {
        resolveProblem()
      }
      console.log('listenBybit: connection open for websocket with ID: ', wsKey, event);
    });

    // Optional: Listen to responses to websocket queries
    // (e.g. the response after subscribing to a topic)
    ws.on('response', (response) => {
      console.log('listenBybit: response', response);
    });

    // Optional: Listen to connection close event.
    // Unexpected connection closes are automatically reconnected.
    ws.on('close', () => {
      console.log('listenBybit: connection closed');
    });

    // Listen to raw error events. Recommended.
    ws.on('exception', (err) => {
      console.error('listenBybit: exception', err);
    });

    ws.on('reconnect', ({ wsKey }) => {
      emitProblem()
      console.log('listenBybit: ws automatically reconnecting.... ', wsKey);
    });

    ws.on('reconnected', (data) => {
      resolveProblem()
      console.log('listenBybit: ws has reconnected ', data?.wsKey);
    });
  }

  private connectBinance(key: TradeKey, ws: BinanceWebsocketClient) {
    let activeWsDisconnectProblem: WsPrivateDisconnectEntry | undefined

    const emitProblem = (wsKey: WsKey) => {
      if (!activeWsDisconnectProblem) {
        activeWsDisconnectProblem = this.logFactory.wsPrivateDisconnect(key, wsKey.startsWith('usdm') ? 'usdm' : undefined)
        this.logger.getKeyLogger(key).add(activeWsDisconnectProblem)
      }
    }
    const resolveProblem = (wsKey: WsKey) => {
      if (activeWsDisconnectProblem) {
        activeWsDisconnectProblem.resolve()
        activeWsDisconnectProblem = undefined
      }
      this.logger.getKeyLogger(key).add(this.logFactory.wsPrivateConnect(key, wsKey.startsWith('usdm') ? 'usdm' : undefined))
    }

    // notification when a connection is opened
    ws.on('open', (data) => {
      resolveProblem(data.wsKey)
      console.log('listenBinance: connection opened open:', data.wsKey, data.ws);
    });

    // receive raw events
    ws.on('message', (data) => {
      console.log('listenBinance: raw message received ', data);
    });

    // receive formatted events with beautified keys. Any "known" floats stored in strings as parsed as floats.
    ws.on('formattedMessage', (data) => {
      console.log('listenBinance: formattedMessage: ', data);
    });

    // receive formatted events with beautified keys. Any "known" floats stored in strings as parsed as floats.
    ws.on('formattedUserDataMessage', (data) => {
      console.log('listenBinance: formattedUserDataMessage: ', data);
    });

    // read response to command sent via WS stream (e.g LIST_SUBSCRIPTIONS)
    ws.on('reply', (data) => {
      console.log('listenBinance: log reply: ', data);
    });

    // receive notification when a ws connection is reconnecting automatically
    ws.on('reconnecting', (data) => {
      emitProblem(data.wsKey)
      console.log('listenBinance: ws automatically reconnecting.... ', data?.wsKey);
    });

    // receive notification that a reconnection completed successfully (e.g use REST to check for missing data)
    ws.on('reconnected', (data) => {
      resolveProblem(data.wsKey)
      console.log('listenBinance: ws has reconnected ', data?.wsKey);
    });

    // Recommended: receive error events (e.g. first reconnection failed)
    ws.on('error', (data) => {
      emitProblem(data.wsKey)
      console.log('listenBinance: ws saw error ', data?.wsKey);
    });

    // Call methods to subscribe to as many websockets as you want.
    // Each method spawns a new connection, unless a websocket already exists for that particular request topic.
    // ws.subscribeSpotAggregateTrades(market);
    // ws.subscribeSpotTrades(market);
    // ws.subscribeSpotKline(market, interval);
    // ws.subscribeSpotSymbolMini24hrTicker(market);
    // ws.subscribeSpotAllMini24hrTickers();
    // ws.subscribeSpotSymbol24hrTicker(market);
    // ws.subscribeSpotAll24hrTickers();
    // ws.subscribeSpotSymbolBookTicker(market);
    // ws.subscribeSpotAllBookTickers();
    // ws.subscribeSpotPartialBookDepth(market, 5);
    // ws.subscribeSpotDiffBookDepth(market);

    //ws.subscribeSpotUserDataStream();
    //ws.subscribeMarginUserDataStream();
    //ws.subscribeIsolatedMarginUserDataStream('BTCUSDT');

    ws.subscribeUsdFuturesUserDataStream()
  }

  private connectOkx(key: TradeKey, ws: OkxWebsocketClient) {
    let connectionSuccess = false
    let activeWsDisconnectProblem: WsPrivateDisconnectEntry | undefined

    const emitProblem = () => {
      connectionSuccess = false
      // Создаём единичную запись о проблеме соединения, пока связь не восстановится.
      if (!activeWsDisconnectProblem) {
        activeWsDisconnectProblem = this.logFactory.wsPrivateDisconnect(key, 'swap')
        this.logger.getKeyLogger(key).add(activeWsDisconnectProblem)
      }
    }
    const resolveProblem = () => {
      // Закрываем запись о проблеме и логируем успешное подключение.
      if (activeWsDisconnectProblem) {
        activeWsDisconnectProblem.resolve()
        activeWsDisconnectProblem = undefined
      }
      this.logger.getKeyLogger(key).add(this.logFactory.wsPrivateConnect(key, 'swap'))
      connectionSuccess = true
    }

    ws.connectPrivate().then(() => {
      ws.subscribe([
        {channel: 'account'},
        {channel: 'positions', instType: 'SWAP'},
        {channel: 'orders', instType: 'SWAP'},
        {channel: 'orders-algo', instType: 'SWAP'},
        //{channel: 'balance_and_position'}
      ], true)
    })

    ws.on('update', (data) => {
      console.log('listenOkx: data received', data);
    });

    // Optional: Listen to websocket connection open event
    // (automatic after subscribing to one or more topics)
    ws.on('open', ({ wsKey, event }) => {
      if (!connectionSuccess) {
        resolveProblem()
      }
      console.log('listenOkx: connection open for websocket with ID: ', wsKey, event);
    });

    // Optional: Listen to responses to websocket queries
    // (e.g. the response after subscribing to a topic)
    ws.on('response', (response) => {
      console.log('listenOkx: response', response);
    });

    // Optional: Listen to connection close event.
    // Unexpected connection closes are automatically reconnected.
    ws.on('close', () => {
      console.log('listenOkx: connection closed');
    });

    // Listen to raw error events. Recommended.
    ws.on('error', (data) => {
      console.error('listenOkx: error', data);
    });

    ws.on('reconnect', (data) => {
      emitProblem()
      console.log('listenOkx: ws automatically reconnecting.... ', data.wsKey);
    });

    ws.on('reconnected', (data) => {
      resolveProblem()
      console.log('listenOkx: ws has reconnected ', data.wsKey);
    });
  }

}
